import { useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { DataGroupBasic } from './data-groups/basic'
import { DataGroupHeadline } from './data-groups/headline'
import { DataGroupIconCounter } from './data-groups/icon-counter'
import { DataGroupIconList } from './data-groups/icon-list'
import { DataGroupIconListCols } from './data-groups/icon-list-cols'
import { DataGroupTable } from './data-groups/table'
import { DetailsCell } from './details-cell'
import { DetailsColumn } from './details-column'
import { DetailsTableWrapper } from './details-table-wrapper'
import { HeadingColumn } from './heading-column'
import { DetailsTableDataGroup, DetailsTableRow } from './models'
import { Row } from './row'
import { ShowMore } from '../show-more/show-more'
import { RestrictableContent } from '../utils/types'

export interface DetailsTableProps extends RestrictableContent {
  /** Rows to be rendered in table */
  rows: DetailsTableRow[]
  /** Toggle whether 'show more' is expanded by default */
  showMoreInitially?: boolean
  /** Number of items to show before requiring 'show more' toggle */
  showCount?: number
  className?: string
}

export function DetailsTable({ rows, showCount = 3, showMoreInitially, isRestricted, className }: DetailsTableProps) {
  const [showMore, setShowMore] = useState(showMoreInitially)
  const displayRows = !showMore ? rows.slice(0, showCount) : rows

  const toggleShowMore = () => {
    setShowMore((prev) => !prev)
  }

  /**
   * The template hierarchy for the table is:
   *
   * Table
   *   Row
   *    Column (static, there's only 2 columns: heading & details)
   *      Cell (only for details column, heading is just title & icon)
   *        DataGroup
   *          Values (optional level, mainly for lists)
   *    subRows if defined
   *      Column (static, there's only 2 columns: heading & details)
   *        Cell (only for details column, heading is just title & icon)
   *          DataGroup
   *            Values (optional level, mainly for lists)
   *
   *
   */

  const getCellTemplate = (item: DetailsTableDataGroup, i: number) => {
    const commonProps = { key: item.id, isFirstGroup: i === 0, ...item }
    switch (item.type) {
      case 'headline':
        return <DataGroupHeadline {...commonProps} />
      case 'iconCounter':
        return <DataGroupIconCounter {...commonProps} />
      case 'iconList':
        return <DataGroupIconList {...commonProps} />
      case 'iconListCols':
        return <DataGroupIconListCols {...commonProps} />
      case 'list':
      case 'table':
        return <DataGroupTable {...commonProps} />
      case 'basic':
      default:
        return <DataGroupBasic {...commonProps} />
    }
  }

  return (
    <DetailsTableWrapper className={className}>
      {displayRows.map((row, i) => {
        return (
          <Row index={i} key={row.title} className="relative">
            {row.editComponent}
            <HeadingColumn icon={row.icon} title={row.title} className={row.className} />

            {/* render rows if no subrows provided */}
            {!row.subRows ? (
              <DetailsColumn>
                {row.cells?.map((cell, i) => (
                  <DetailsCell
                    isLastCell={row.cells?.length === i + 1}
                    isOnlyCell={row.cells?.length === 1}
                    className={twMerge(cell.cellClassName, isRestricted ? 'select-none blur-sm' : '')}
                    key={cell.id}
                  >
                    {cell.data.map((item, i) => getCellTemplate(item, i))}
                  </DetailsCell>
                ))}
              </DetailsColumn>
            ) : null}

            {/* render subrows if provided */}
            {row.subRows ? (
              <div className="flex-grow">
                {row.subRows.map((subRow) => (
                  <div
                    key={subRow[0].id}
                    id="subRow"
                    className="border-b border-dashed border-shades-200 last:border-0"
                  >
                    <DetailsColumn>
                      {subRow.map((cell, i) => (
                        <DetailsCell
                          isLastCell={subRow?.length === i + 1}
                          isOnlyCell={subRow?.length === 1}
                          className={twMerge(cell.cellClassName, isRestricted ? 'select-none blur-sm' : '')}
                          key={cell.id}
                        >
                          {cell.data.map((item, i) => getCellTemplate(item, i))}
                        </DetailsCell>
                      ))}
                    </DetailsColumn>
                  </div>
                ))}
              </div>
            ) : null}
          </Row>
        )
      })}
      {rows.length > showCount ? <ShowMore onClick={toggleShowMore} showMore={showMore} /> : null}
    </DetailsTableWrapper>
  )
}

export default DetailsTable
